import type { EffectiveTax } from '@monorepo/shared/schemas/taxes'
import { Shield } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { TooltipProvider } from '@/shared/components/ui/tooltip'
import { type AssignedTax, AssignedTaxesList } from './AssignedTaxesList'

interface TaxesSectionProps {
  taxes: EffectiveTax[]
  onAddTax?: () => void
  onRemoveTax?: (taxId: string) => void
  showActions?: boolean
  taxExempt?: boolean
  taxExemptionCertificate?: string | null
  taxExemptionReason?: string | null
}

export function TaxesSection({
  taxes,
  onAddTax,
  onRemoveTax,
  showActions = true,
  taxExempt = false,
  taxExemptionCertificate,
  taxExemptionReason,
}: TaxesSectionProps) {
  // Convert EffectiveTax to AssignedTax format
  const assignedTaxes: AssignedTax[] = taxes.map(tax => ({
    id: tax.id,
    tenant_id: '', // Not displayed in UI
    environment_id: '', // Not displayed in UI
    name: tax.name,
    code: tax.code,
    description: null,
    tax_type: tax.tax_type,
    percentage_value: tax.percentage_value ?? null,
    fixed_amount_cents: tax.fixed_amount_cents ?? null,
    currency: null,
    applies_to: null,
    taxable_amount_type: null,
    compound: false,
    jurisdiction: null,
    jurisdiction_level: null,
    tax_authority: null,
    external_tax_service: null,
    external_tax_id: null,
    effective_from: null,
    effective_to: null,
    auto_apply: false,
    is_active: tax.is_active ?? true,
    metadata: {},
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    // AssignedTax-specific fields
    priority: tax.priority || 0,
    assigned_at: tax.assigned_at || new Date().toISOString(),
    source: tax.source,
  }))

  return (
    <Card className='bg-transparent border'>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div>
            <CardTitle className='flex items-center gap-2'>
              Taxes
              {taxExempt && (
                <Badge variant='outline' className='text-xs'>
                  <Shield className='w-3 h-3 mr-1' />
                  Tax Exempt
                </Badge>
              )}
            </CardTitle>
            {taxExempt && (taxExemptionCertificate || taxExemptionReason) && (
              <p className='text-sm text-muted-foreground mt-1'>
                {taxExemptionCertificate &&
                  `Certificate: ${taxExemptionCertificate}`}
                {taxExemptionReason && ` - ${taxExemptionReason}`}
              </p>
            )}
          </div>
          {onAddTax && !taxExempt && (
            <Button onClick={onAddTax} variant='outline' size='sm'>
              Add Tax
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {taxExempt ? (
          <div className='text-center py-8 text-muted-foreground'>
            <Shield className='w-12 h-12 mx-auto mb-3 text-muted-foreground/50' />
            <p>This customer is tax-exempt</p>
            <p className='text-sm mt-1'>No taxes will be applied to invoices</p>
          </div>
        ) : (
          <TooltipProvider>
            <AssignedTaxesList
              taxes={assignedTaxes}
              onRemove={showActions ? onRemoveTax : undefined}
              readonly={!showActions}
              showSource
              emptyMessage={
                onAddTax
                  ? 'Click "Add Tax" to assign taxes to this customer'
                  : 'This customer has no specific tax assignments and will use organization defaults if available'
              }
            />
          </TooltipProvider>
        )}
      </CardContent>
    </Card>
  )
}
